<template>
  <section v-if="visible" class="offering-book">
    <header class="offering-book__head">
      <div>
        <h2 class="offering-book__title">How this offer priced</h2>
        <p class="offering-book__lead">{{ lead }}</p>
      </div>
      <span v-if="statusLabel" class="offering-book__badge" :class="`offering-book__badge--${statusTone}`">
        {{ statusLabel }}
      </span>
    </header>

    <!-- Each tranche of a combined offering is its own book, against its own size. Reading them
         as one would report a raise as covered while a slice of it sat empty. -->
    <div v-if="tranches.length > 1" class="offering-book__tranches" role="tablist">
      <button
        v-for="t in tranches"
        :key="t.key"
        class="offering-book__tranche"
        :class="{ 'offering-book__tranche--on': t.key === selectedTranche }"
        type="button"
        role="tab"
        :aria-selected="t.key === selectedTranche"
        @click="selectTranche(t.key)"
      >
        {{ t.name || t.key }}
      </button>
    </div>

    <dl class="offering-book__stats">
      <div>
        <dt>Total demand</dt>
        <dd>{{ money(summary.total_demand) }}</dd>
      </div>
      <div>
        <dt>Offer size</dt>
        <dd>{{ money(offerSize) }}</dd>
      </div>
      <div>
        <dt>Cover</dt>
        <dd>{{ coverLabel }}</dd>
      </div>
      <div>
        <dt>Bids</dt>
        <dd>
          {{ summary.commitment_count }}
          <span v-if="strikeAmount > 0" class="offering-book__note">
            {{ money(strikeAmount) }} at any {{ noun }}
          </span>
        </dd>
      </div>
    </dl>

    <div v-if="ladder.length" class="offering-book__ladder">
      <h3 class="offering-book__subtitle">Demand at each {{ noun }}</h3>

      <div class="offering-book__scroll">
        <table class="offering-book__table">
          <thead>
            <tr>
              <th scope="col">{{ isRateBased ? "Rate" : "Price" }}</th>
              <th scope="col" class="num">At this {{ noun }}</th>
              <th scope="col" class="num">Cumulative</th>
              <th scope="col" class="num">Bids</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(row, i) in ladder" :key="row.level">
              <tr :class="{ 'is-filled': rowQualifies(row) }">
                <!-- The cumulative bar is drawn behind the row rather than in its own column:
                     the shape of the curve is the point, and a fifth column of numbers buries it. -->
                <td class="lvl">
                  <span class="offering-book__bar" :style="{ width: barWidth(row) }"></span>
                  <span class="lvl__text">{{ formatLevel(row.level) }}</span>
                </td>
                <td class="num">{{ money(row.amount_at) }}</td>
                <td class="num">{{ money(row.amount_cumulative) }}</td>
                <td class="num">{{ row.count }}</td>
              </tr>
              <tr v-if="i === markerIndex" class="offering-book__marker">
                <td colspan="4">{{ markerLabel }}</td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <p v-if="strikeAmount > 0" class="offering-book__footnote">
        Bids placed at the final {{ noun }} named no level, so they are eligible at every rung and
        are counted in every cumulative figure above.
      </p>
    </div>

    <p v-else class="offering-book__empty">{{ emptyLabel }}</p>
  </section>
</template>

<script>
/**
 * The book, as an investor sees it — and only after bidding has stopped.
 *
 * The gate is hasResolvedBook(), and the reasoning for it lives there. In short: showing a live
 * cumulative ladder to people who are still bidding destroys the price discovery the book exists
 * to perform. Once closed, the same numbers are the demand curve the price was set against, and
 * the only thing that explains a scale-back to the investor who was scaled back.
 *
 * The payload is self-contained on tokenId — currency, offer size, guidance and tranches all
 * arrive with it — so this fetches its own data rather than being fed from the deal page. The
 * one thing it reaches to the token for is the final price/rate, which the book does not carry.
 */
import { mapState } from "vuex";
import { convertToLocale, formatAmountToDollar } from "@/utils/helpers.js";
import {
  SUBSCRIPTION_STATUS_LABELS,
  AuctionPricing,
  OfferingTypes,
  SubscriptionStatuses,
  formatOfferMoment,
  hasResolvedBook,
} from "@/utils/offering";

export default {
  name: "OfferingBook",

  props: {
    token: { type: Object, required: true },
  },

  data() {
    return { selectedTranche: "" };
  },

  computed: {
    ...mapState({ book: (state) => state.TokenModule.offeringBook }),

    summary() {
      return this.book?.summary || null;
    },

    offering() {
      return this.book?.offering || null;
    },

    // Two gates, not one. hasResolvedBook decides whether to ask at all; is_live is the server
    // saying whether bidding has really stopped, and it wins.
    visible() {
      return Boolean(
        hasResolvedBook(this.token) && this.summary && this.summary.is_live !== true
      );
    },

    isRateBased() {
      return Boolean(this.offering?.is_rate_based);
    },

    noun() {
      return this.isRateBased ? "rate" : "price";
    },

    currency() {
      return this.offering?.currency || this.token?.currency || "USD";
    },

    // The tranche's size when one is selected, so cover describes the book actually on screen.
    offerSize() {
      return Number(this.offering?.raise_amount || 0);
    },

    tranches() {
      return this.offering?.tranches || [];
    },

    ladder() {
      return this.book?.ladder || [];
    },

    strikeAmount() {
      return Number(this.summary?.strike_bid_amount || 0);
    },

    statusLabel() {
      return SUBSCRIPTION_STATUS_LABELS[this.summary?.subscription_status] || "";
    },

    statusTone() {
      return this.summary?.subscription_status === SubscriptionStatuses.UNDERSUBSCRIBED
        ? "warn"
        : "good";
    },

    coverLabel() {
      const cover = Number(this.summary?.cover_ratio);
      return Number.isFinite(cover) ? `${cover.toFixed(2)}×` : "—";
    },

    closeLabel() {
      return formatOfferMoment(
        this.offering?.offer_closes_at || this.token?.offer_closes_at,
        this.offering?.market_timezone || this.token?.market_timezone
      );
    },

    lead() {
      return `Bidding closed ${this.closeLabel}. This is the demand the ${this.noun} was set against.`;
    },

    /**
     * The level the offering actually priced at, which the book payload does not carry.
     *
     * A tranche prices on its own, so a selected tranche's level wins over the offering's.
     */
    finalLevel() {
      const tranche = this.tranches.find((t) => t.key === this.selectedTranche);
      const source = tranche || this.token || {};
      const level = this.isRateBased ? source.final_rate : source.final_price;
      return Number(level) > 0 ? Number(level) : null;
    },

    // Where the cumulative first covers the offer. Only interesting before pricing — after it,
    // the final level is the real boundary and this is a guess at it.
    coverIndex() {
      if (!(this.offerSize > 0)) return -1;
      return this.ladder.findIndex((row) => Number(row.amount_cumulative) >= this.offerSize);
    },

    markerIndex() {
      if (this.finalLevel !== null) {
        // Last row that still qualifies — the boundary sits under it.
        let last = -1;
        this.ladder.forEach((row, i) => {
          if (this.rowQualifies(row)) last = i;
        });
        return last;
      }
      return this.coverIndex;
    },

    /**
     * Whether each winner settled at their own bid rather than at the clearing level.
     *
     * Read off the book payload, which carries `auction_pricing` only when the offering has one.
     * Auctions only: a book build clears every bid at a single level by construction.
     */
    payYourBid() {
      const type = this.offering?.offering_type || this.token?.offering_type;
      return (
        type === OfferingTypes.AUCTION
        && this.offering?.auction_pricing === AuctionPricing.MULTIPLE
      );
    },

    /**
     * The boundary, and what being inside it actually got you.
     *
     * The qualifying level alone is only half the answer: "bids at or below 19% were allotted"
     * says who was filled, not at what. Under pay-your-bid those bidders each settle at their
     * own level, and reading the clearing figure as the price they paid would be wrong for
     * every one of them who bid away from it.
     */
    markerLabel() {
      if (this.finalLevel === null) return "The offer is covered at this level.";

      const level = this.formatLevel(this.finalLevel);
      const boundary = `Final ${this.noun} ${level} — bids at or ${
        this.isRateBased ? "below" : "above"
      } this were allotted`;

      return this.payYourBid
        ? `${boundary}, each at its own ${this.noun} rather than at ${level}.`
        : `${boundary}, every one of them at ${level}.`;
    },

    emptyLabel() {
      if (this.strikeAmount > 0) {
        return `Every bid was placed at the final ${this.noun}, so there are no levels to show.`;
      }
      return "No bids were placed in this book.";
    },
  },

  watch: {
    // The deal page can resolve its token after this mounts.
    "token._id": {
      handler(id) {
        if (id) this.fetch();
      },
    },
  },

  mounted() {
    this.fetch();
  },

  methods: {
    fetch() {
      // Asking at all is the thing to get right: a live book must not be requested, however it
      // would be rendered.
      if (!hasResolvedBook(this.token) || !this.token?._id) return;
      this.$store.dispatch("TokenModule/getOfferingBook", {
        id: this.token._id,
        tranche: this.selectedTranche || undefined,
      });
    },

    selectTranche(key) {
      if (key === this.selectedTranche) return;
      this.selectedTranche = key;
      this.fetch();
    },

    /**
     * Whether a bid at this level was filled.
     *
     * Inverts by instrument: an equity issuer fills from the highest price down, a bond issuer
     * from the lowest rate up.
     */
    rowQualifies(row) {
      if (this.finalLevel === null) return false;
      const level = Number(row.level);
      return this.isRateBased ? level <= this.finalLevel : level >= this.finalLevel;
    },

    barWidth(row) {
      const basis = Math.max(this.offerSize, Number(this.summary?.total_demand || 0));
      if (!(basis > 0)) return "0%";
      const pct = (Number(row.amount_cumulative) / basis) * 100;
      return `${Math.min(Math.max(pct, 0), 100)}%`;
    },

    formatLevel(level) {
      return this.isRateBased ? `${convertToLocale(level)}%` : this.money(level);
    },

    money(value) {
      return formatAmountToDollar(Number(value || 0), 2, this.currency);
    },
  },
};
</script>

<style lang="scss" scoped>
.offering-book {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;

  &__head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    flex-wrap: wrap;
  }

  &__title {
    font-size: 16px;
    font-weight: 600;
    color: #0f172a;
  }

  &__lead {
    margin-top: 4px;
    font-size: 13px;
    color: #64748b;
  }

  &__badge {
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;

    &--good {
      background: #ecfdf5;
      color: #047857;
    }

    &--warn {
      background: #fffbeb;
      color: #b45309;
    }
  }

  &__tranches {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 16px;
  }

  &__tranche {
    padding: 6px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 999px;
    background: #ffffff;
    font-size: 13px;
    color: #475569;
    cursor: pointer;

    &--on {
      border-color: #4d84ff;
      background: #eff6ff;
      color: #1d4ed8;
      font-weight: 500;
    }
  }

  &__stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 14px 20px;
    margin-top: 18px;
    padding-top: 18px;
    border-top: 1px solid #f1f5f9;

    dt {
      font-size: 12px;
      color: #64748b;
    }

    dd {
      margin-top: 2px;
      font-size: 15px;
      font-weight: 600;
      color: #0f172a;
    }
  }

  &__note {
    display: block;
    font-size: 12px;
    font-weight: 400;
    color: #64748b;
  }

  &__subtitle {
    font-size: 14px;
    font-weight: 600;
    color: #0f172a;
    margin-bottom: 10px;
  }

  &__ladder {
    margin-top: 22px;
  }

  // Wide content scrolls inside its own box rather than pushing the page sideways.
  &__scroll {
    overflow-x: auto;
  }

  &__table {
    width: 100%;
    min-width: 460px;
    border-collapse: collapse;
    font-size: 13px;

    th {
      text-align: left;
      font-weight: 500;
      color: #64748b;
      padding: 8px 10px;
      border-bottom: 1px solid #e2e8f0;
      white-space: nowrap;
    }

    td {
      padding: 10px;
      color: #475569;
      border-bottom: 1px solid #f1f5f9;
    }

    .num {
      text-align: right;
      font-variant-numeric: tabular-nums;
    }

    // A filled bid is the one an investor cares whether they made, so it carries the ink.
    tr.is-filled td {
      color: #0f172a;
      font-weight: 500;
    }

    .lvl {
      position: relative;
      isolation: isolate;
      white-space: nowrap;

      &__text {
        position: relative;
      }
    }
  }

  &__bar {
    position: absolute;
    left: 0;
    top: 4px;
    bottom: 4px;
    background: #eff6ff;
    border-radius: 3px;
    z-index: -1;
  }

  &__marker td {
    padding: 8px 10px;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    font-size: 12px;
    font-weight: 500;
    color: #1d4ed8;
  }

  &__footnote,
  &__empty {
    margin-top: 12px;
    font-size: 12px;
    color: #64748b;
  }
}
</style>
